import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080';
const USER_API = `${CONFERENCE_API}/api/submission-reviewers`;

const ReviewerManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [conferences, setConferences] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [alert, setAlert] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'REVIEWER',
    trackIds: []
  });

  const [errors, setErrors] = useState({});

  const roles = ['REVIEWER', 'CHAIR', 'ADMIN'];

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(USER_API);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      } else {
        showAlert('Failed to fetch users', 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showAlert('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users by role
  const fetchUsersByRole = async (role) => {
    if (!role) {
      setFilteredUsers(users);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${USER_API}/role/${encodeURIComponent(role)}`);
      if (response.ok) {
        const data = await response.json();
        setFilteredUsers(data);
      } else {
        showAlert('Failed to fetch users by role', 'error');
      }
    } catch (error) {
      console.error('Error fetching users by role:', error);
      showAlert('Error fetching users by role', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchConferences();
  }, []);

  const fetchConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        
        // Filter conferences that have abstract submissions enabled
        const conferencesWithAbstracts = await Promise.all(
          data.map(async (conf) => {
            try {
              const metadataResponse = await fetch(`${CONFERENCE_API}/api/abstract-metadata/conference/${conf.id}`);
              if (metadataResponse.ok) {
                const metadata = await metadataResponse.json();
                // Check if abstract metadata exists (indicating abstract submissions are enabled)
                return metadata && (Array.isArray(metadata) ? metadata.length > 0 : true) ? conf : null;
              }
            } catch (err) {
              console.error(`Error checking abstract metadata for conference ${conf.id}:`, err);
            }
            return null;
          })
        );
        
        // Filter out null values (conferences without abstract submissions)
        const filteredConferences = conferencesWithAbstracts.filter(conf => conf !== null);
        setConferences(filteredConferences);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchTracks = async (conferenceId) => {
    if (!conferenceId) {
      setTracks([]);
      return;
    }

    try {
      const response = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conferenceId}`);
      if (response.ok) {
        const data = await response.json();
        setTracks(data);
      } else {
        setTracks([]);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
      setTracks([]);
    }
  };

  useEffect(() => {
    if (selectedRole) {
      fetchUsersByRole(selectedRole);
    } else {
      setFilteredUsers(users);
    }
  }, [selectedRole, users]);

  useEffect(() => {
    fetchTracks(selectedConferenceId);
  }, [selectedConferenceId]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOpenModal = (mode, user = null) => {
    setModalMode(mode);
    if (mode === 'edit' && user) {
      setEditingUser(user);
      setFormData({
        fullName: user.fullName,
        email: user.email,
        role: user.role,
        trackIds: user.trackIds || []
      });
      // If user has tracks, we need to determine which conference they belong to
      if (user.trackIds && user.trackIds.length > 0) {
        // Find the conference for the first track to pre-select
        const findConferenceForTrack = async () => {
          for (const conf of conferences) {
            const response = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conf.id}`);
            if (response.ok) {
              const confTracks = await response.json();
              if (confTracks.some(t => user.trackIds.includes(t.id))) {
                setSelectedConferenceId(conf.id.toString());
                fetchTracks(conf.id);
                break;
              }
            }
          }
        };
        findConferenceForTrack();
      }
    } else {
      setEditingUser(null);
      setFormData({
        fullName: '',
        email: '',
        role: 'REVIEWER',
        trackIds: []
      });
      setSelectedConferenceId('');
      setTracks([]);
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      fullName: '',
      email: '',
      role: 'REVIEWER',
      trackIds: []
    });
    setSelectedConferenceId('');
    setTracks([]);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const url = modalMode === 'create' 
        ? USER_API 
        : `${USER_API}/${editingUser.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Trim all string values before submitting
      const trimmedData = {
        fullName: formData.fullName?.trim(),
        email: formData.email?.trim(),
        role: formData.role?.trim(),
        trackIds: (formData.trackIds || []).length > 0 ? formData.trackIds : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trimmedData)
      });

      if (response.ok) {
        const message = modalMode === 'create' 
          ? 'User created successfully! A password setup email has been sent.'
          : 'User updated successfully!';
        showAlert(message, 'success');
        handleCloseModal();
        fetchUsers();
      } else {
        const errorText = await response.text();
        showAlert(errorText || `Failed to ${modalMode} user`, 'error');
      }
    } catch (error) {
      console.error(`Error ${modalMode}ing user:`, error);
      showAlert(`Error ${modalMode}ing user`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${USER_API}/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 204) {
        showAlert('User deleted successfully!', 'success');
        fetchUsers();
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to delete user', 'error');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showAlert('Error deleting user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return '#dc3545';
      case 'CHAIR':
        return '#ffc107';
      case 'REVIEWER':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await fetch(`${USER_API}/bulk-upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);
        setShowUploadModal(true);
        fetchUsers();
        showAlert(`Upload completed: ${result.success} successful, ${result.failed} failed`, 
          result.failed === 0 ? 'success' : 'warning');
      } else {
        const error = await response.json();
        showAlert(error.error || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showAlert('Error uploading file', 'error');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleDownloadCSV = () => {
    const csvContent = [
      ['Full Name', 'Email', 'Role', 'Track ID', 'Track Name'],
      ...filteredUsers.map(user => [user.fullName, user.email, user.role, (user.trackIds || []).join(';'), (user.trackNames || []).join(';')])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviewers_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'fullName,email,role,trackId\nJohn Doe,john@example.com,REVIEWER,5\nJane Smith,jane@example.com,CHAIR,\nBob Wilson,bob@example.com,REVIEWER,"7;8;9"';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reviewer_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTrackReference = async () => {
    try {
      // Fetch all conferences with abstracts
      const confResponse = await fetch(`${CONFERENCE_API}/active`);
      if (!confResponse.ok) {
        showAlert('Failed to fetch conferences', 'error');
        return;
      }
      
      const allConferences = await confResponse.json();
      const trackData = [];
      
      // Header row
      trackData.push(['Conference ID', 'Conference Name', 'Track ID', 'Track Name']);
      
      // Fetch tracks for each conference
      for (const conf of allConferences) {
        try {
          // Check if conference has abstract submissions
          const metadataResponse = await fetch(`${CONFERENCE_API}/api/abstract-metadata/conference/${conf.id}`);
          if (metadataResponse.ok) {
            const metadata = await metadataResponse.json();
            const hasAbstracts = metadata && (Array.isArray(metadata) ? metadata.length > 0 : true);
            
            if (hasAbstracts) {
              // Fetch tracks for this conference
              const tracksResponse = await fetch(`${CONFERENCE_API}/api/tracks/conference/${conf.id}`);
              if (tracksResponse.ok) {
                const tracks = await tracksResponse.json();
                
                if (tracks.length > 0) {
                  tracks.forEach(track => {
                    trackData.push([conf.id, conf.name, track.id, track.name]);
                  });
                } else {
                  // Conference with no tracks
                  trackData.push([conf.id, conf.name, '', 'No tracks defined']);
                }
              }
            }
          }
        } catch (err) {
          console.error(`Error fetching tracks for conference ${conf.id}:`, err);
        }
      }
      
      // Create CSV content
      const csvContent = trackData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `track_reference_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      showAlert('Track reference downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading track reference:', error);
      showAlert('Error downloading track reference', 'error');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Reviewer & User Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleDownloadTrackReference}
            style={{
              padding: '10px 20px',
              backgroundColor: '#9c27b0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            title="Download track reference with IDs for CSV upload"
          >
            📋 Track IDs
          </button>
          <button
            onClick={handleDownloadTemplate}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            title="Download CSV template for bulk upload"
          >
            📥 Template
          </button>
          <label style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'inline-block'
          }}>
            📤 Upload CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
          <button
            onClick={handleDownloadCSV}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📊 Export CSV
          </button>
          <button
            onClick={() => handleOpenModal('create')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            + Add User
          </button>
        </div>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Filter by Role:
        </label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '300px',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">All Roles</option>
          {roles.map(role => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

      {!loading && filteredUsers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No users found. Click "Add User" to create one.
        </div>
      )}

      {!loading && filteredUsers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Full Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Conferences</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Expert Track</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{user.id}</td>
                  <td style={{ padding: '12px' }}>{user.fullName}</td>
                  <td style={{ padding: '12px' }}>{user.email}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: getRoleBadgeColor(user.role),
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {user.conferenceNames && user.conferenceNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.conferenceNames.map((name, index) => (
                          <span key={index} style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            fontSize: '11px'
                          }}>
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {user.trackNames && user.trackNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.trackNames.map((name, index) => (
                          <span key={index} style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontSize: '11px'
                          }}>
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenModal('edit', user)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffc107',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              {modalMode === 'create' ? 'Add User' : 'Edit User'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Full Name: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="e.g., John Doe"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.fullName ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {errors.fullName && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.fullName}</span>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Email: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="e.g., john.doe@example.com"
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.email ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: modalMode === 'edit' ? '#f5f5f5' : 'white'
                  }}
                />
                {errors.email && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.email}</span>
                )}
              </div>

              {modalMode === 'create' && (
                <div style={{
                  marginBottom: '20px',
                  padding: '12px',
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: '#004085'
                }}>
                  <strong>ℹ️ Password Setup:</strong> A secure password setup link will be sent to the user's email address. The user will set their own password.
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Role: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.role ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  {roles.map(role => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.role}</span>
                )}
              </div>

              {formData.role === 'REVIEWER' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      Select Conference:
                    </label>
                    <select
                      value={selectedConferenceId}
                      onChange={(e) => {
                        setSelectedConferenceId(e.target.value);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">-- Select Conference --</option>
                      {conferences.map(conf => (
                        <option key={conf.id} value={conf.id}>
                          {conf.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      Expert Tracks:
                    </label>
                    {selectedConferenceId && tracks.length > 0 ? (
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '10px'
                      }}>
                        {tracks.map(track => (
                          <label
                            key={track.id}
                            style={{
                              display: 'block',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              marginBottom: '4px',
                              border: (formData.trackIds || []).includes(track.id) ? '2px solid #28a745' : '1px solid transparent',
                              backgroundColor: (formData.trackIds || []).includes(track.id) ? '#e8f5e9' : 'transparent'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={(formData.trackIds || []).includes(track.id)}
                              onChange={() => {
                                setFormData(prev => {
                                  const currentTrackIds = prev.trackIds || [];
                                  const trackIds = currentTrackIds.includes(track.id)
                                    ? currentTrackIds.filter(id => id !== track.id)
                                    : [...currentTrackIds, track.id];
                                  return { ...prev, trackIds };
                                });
                              }}
                              style={{ marginRight: '8px' }}
                            />
                            {track.name}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        padding: '20px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        {!selectedConferenceId ? 'Please select a conference first' : 'No tracks available for this conference'}
                      </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      Select the tracks this reviewer is an expert in (optional). Selected tracks: {(formData.trackIds || []).length}
                    </p>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadModal && uploadResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Upload Results
            </h3>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Total Records:</strong> {uploadResult.total}
              </div>
              <div style={{ marginBottom: '10px', color: '#28a745' }}>
                <strong>✓ Successful:</strong> {uploadResult.success}
              </div>
              <div style={{ color: '#dc3545' }}>
                <strong>✗ Failed:</strong> {uploadResult.failed}
              </div>
            </div>

            {uploadResult.errorList && uploadResult.errorList.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#dc3545', marginBottom: '10px' }}>Errors:</h4>
                <div style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {uploadResult.errorList.map((error, index) => (
                    <div key={index} style={{ 
                      marginBottom: '10px', 
                      padding: '10px', 
                      backgroundColor: '#f8d7da',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      <div><strong>Row {error.row}:</strong> {error.error}</div>
                      <div style={{ marginTop: '5px', color: '#666', fontStyle: 'italic' }}>
                        Data: {error.data}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadResult.successList && uploadResult.successList.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#28a745', marginBottom: '10px' }}>
                  Successfully Created/Updated Users:
                </h4>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {uploadResult.successList.map((user, index) => (
                    <div key={index} style={{ 
                      marginBottom: '8px', 
                      padding: '8px', 
                      backgroundColor: '#d4edda',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      {user.fullName} ({user.email}) - {user.role}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadResult(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerManagement;
